import React from 'react';
import { Search, RotateCcw, EyeOff, Eye } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { VARIANTS, RARITIES, SEASONS } from '../mock';

const statusOptions = [
  { value: 'all', label: 'All', color: 'bg-[#3a1064]' },
  { value: 'owned', label: 'Owned', color: 'bg-emerald-500' },
  { value: 'missing', label: 'Missing', color: 'bg-[#ff2bd6]' },
  { value: 'mastered', label: 'Mastered', color: 'bg-gradient-to-r from-yellow-400 via-pink-400 to-cyan-400' },
];

export default function FilterBar({ filters, setFilters, onReset }) {
  const setField = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 mt-6">
      <div
        className="rounded-2xl border-[3px] shadow-[0_5px_0_var(--t-card-border)] p-4 md:p-5"
        style={{ background: 'var(--t-card)', borderColor: 'var(--t-card-border)' }}
      >
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--t-text)' }} />
            <Input
              placeholder="Search a sprite (e.g. Water, Galaxy, Mythic)..."
              value={filters.q}
              onChange={(e) => setField('q', e.target.value)}
              className="pl-9 h-11 rounded-xl border-2 focus-visible:ring-[#ff2bd6] font-bold placeholder:opacity-60"
              style={{
                borderColor: 'var(--t-card-border)',
                background: 'var(--t-input-bg)',
                color: 'var(--t-text)',
              }}
            />
          </div>

          {/* Status pills */}
          <div
            className="inline-flex rounded-xl p-1 border-2"
            style={{ background: 'var(--t-muted-bg)', borderColor: 'var(--t-card-border)' }}
          >
            {statusOptions.map((o) => (
              <button
                key={o.value}
                onClick={() => setField('status', o.value)}
                className={`px-3 h-9 text-xs font-black uppercase tracking-wide rounded-lg transition-all ${
                  filters.status === o.value
                    ? `${o.color} text-white shadow-[0_2px_0_var(--t-card-border)]`
                    : 'hover:opacity-80'
                }`}
                style={
                  filters.status === o.value
                    ? undefined
                    : { color: 'var(--t-text)' }
                }
              >
                {o.label}
              </button>
            ))}
          </div>

          {/* Season */}
          <Select value={filters.season} onValueChange={(v) => setField('season', v)}>
            <SelectTrigger
              className="h-11 rounded-xl w-[210px] border-2 font-black"
              style={{ borderColor: 'var(--t-card-border)', background: 'var(--t-input-bg)', color: 'var(--t-text)' }}
              data-testid="filter-season"
            >
              <SelectValue placeholder="Season" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Seasons</SelectItem>
              {SEASONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}{s.current ? ' (Current)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Variant */}
          <Select value={filters.variant} onValueChange={(v) => setField('variant', v)}>
            <SelectTrigger
              className="h-11 rounded-xl w-[150px] border-2 font-black"
              style={{ borderColor: 'var(--t-card-border)', background: 'var(--t-input-bg)', color: 'var(--t-text)' }}
            >
              <SelectValue placeholder="Variant" data-testid="filter-variant-value" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All variants</SelectItem>
              {Object.entries(VARIANTS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Rarity */}
          <Select value={filters.rarity} onValueChange={(v) => setField('rarity', v)}>
            <SelectTrigger
              className="h-11 rounded-xl w-[150px] border-2 font-black"
              style={{ borderColor: 'var(--t-card-border)', background: 'var(--t-input-bg)', color: 'var(--t-text)' }}
            >
              <SelectValue placeholder="Rarity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All rarities</SelectItem>
              {Object.entries(RARITIES).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort */}
          <Select value={filters.sort} onValueChange={(v) => setField('sort', v)}>
            <SelectTrigger
              className="h-11 rounded-xl w-[180px] border-2 font-black"
              style={{ borderColor: 'var(--t-card-border)', background: 'var(--t-input-bg)', color: 'var(--t-text)' }}
            >
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="type">Sort: Type</SelectItem>
              <SelectItem value="level-desc">Level: High to Low</SelectItem>
              <SelectItem value="level-asc">Level: Low to High</SelectItem>
              <SelectItem value="rarity-desc">Rarity: High to Low</SelectItem>
              <SelectItem value="rarity-asc">Rarity: Low to High</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            className={`h-11 rounded-xl border-2 hover:opacity-90 font-black uppercase tracking-wide text-xs ${
              filters.showUnreleased
                ? 'bg-[#ff2bd6] text-white border-[#3a1064] shadow-[0_3px_0_#3a1064]'
                : ''
            }`}
            style={
              filters.showUnreleased
                ? undefined
                : { borderColor: 'var(--t-card-border)', background: 'var(--t-muted-bg)', color: 'var(--t-text)' }
            }
            onClick={() => setField('showUnreleased', !filters.showUnreleased)}
            data-testid="toggle-show-unreleased"
            aria-pressed={filters.showUnreleased}
            title={filters.showUnreleased ? 'Hiding unreleased sprites' : 'Show unreleased sprites'}
          >
            {filters.showUnreleased ? (
              <Eye className="h-4 w-4 mr-2" />
            ) : (
              <EyeOff className="h-4 w-4 mr-2" />
            )}
            {filters.showUnreleased ? 'Hide Unreleased' : 'Show Unreleased'}
          </Button>

          <Button
            variant="outline"
            className="h-11 rounded-xl border-2 hover:opacity-90 font-black"
            style={{ borderColor: 'var(--t-card-border)', background: 'var(--t-muted-bg)', color: 'var(--t-text)' }}
            onClick={onReset}
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reset
          </Button>
        </div>
      </div>
    </section>
  );
}
